import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Radio, Loader2, CheckCircle, Music, Video, User, Mic, Camera, Palette, Headphones } from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

const FORMAT_OPTIONS = [
  { value: 'audio', label: 'Audio', icon: Music },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'live', label: 'Live Performance', icon: Mic },
  { value: 'collab', label: 'Collaboration', icon: User },
  { value: 'interview', label: 'Interview', icon: Headphones },
];

const TYPE_ICONS = {
  song: Music,
  track: Music,
  video: Video,
  creator: User,
  artist: User,
  podcast: Mic,
  podcaster: Mic,
  stream: Camera,
  streamer: Camera,
  service: Palette,
  studio: Headphones,
  producer: Music,
  videographer: Camera,
  graphic_designer: Palette,
};

/**
 * Universal Request Button Component
 * 
 * Usage:
 * <RequestButton 
 *   targetType="song" 
 *   targetId="track_abc123" 
 *   targetName="Song Title"
 *   creatorName="Artist Name"
 *   variant="default"    // default, outline, ghost, icon
 *   size="default"       // default, sm, lg, icon
 *   className=""
 * />
 */
export function RequestButton({ 
  targetType, 
  targetId, 
  targetName,
  creatorName,
  variant = 'default',
  size = 'default',
  className = '',
  showLabel = true,
  disabled = false
}) {
  const { isAuthenticated, getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    message: '',
    dedication: '',
    reason: '',
    format_preference: ''
  });

  const TypeIcon = TYPE_ICONS[targetType?.toLowerCase()] || Radio;

  const handleClick = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    if (!isAuthenticated) {
      toast.info('Sign in to make requests');
      // Store intent for after login
      sessionStorage.setItem('pendingRequest', JSON.stringify({ targetType, targetId, targetName }));
      navigate('/login');
      return;
    }
    
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (submitting) return;
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/requests/universal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          message: formData.message || null,
          dedication: formData.dedication || null,
          reason: formData.reason || null,
          format_preference: formData.format_preference || null
        })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        setSuccess(true);
        toast.success(result.data.message || `Request sent to ${creatorName || 'creator'}!`);
        
        // Reset and close after animation
        setTimeout(() => {
          setIsOpen(false);
          setSuccess(false);
          setFormData({ message: '', dedication: '', reason: '', format_preference: '' });
        }, 1500);
      } else {
        toast.error(result.data?.detail || result.error || 'Failed to send request');
      }
    } catch (err) {
      console.error('Request error:', err?.message || err);
      if (err?.name === 'TypeError') {
        toast.error('Network error. Check your connection and try again.');
      } else {
        toast.error('Failed to send request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Icon-only button for compact layouts
  if (size === 'icon' || !showLabel) {
    return (
      <>
        <Button
          variant={variant}
          size="icon"
          onClick={handleClick}
          disabled={disabled}
          className={`hover:text-primary hover:scale-105 transition-all ${className}`}
          data-testid={`request-button-${targetType}-${targetId}`}
          title="Request"
        >
          <Radio className="w-4 h-4" />
        </Button>
        
        <RequestModal 
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          success={success}
          formData={formData}
          setFormData={setFormData}
          targetType={targetType}
          targetName={targetName}
          creatorName={creatorName}
          TypeIcon={TypeIcon}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={disabled}
        className={`gap-2 hover:scale-105 transition-all ${className}`}
        data-testid={`request-button-${targetType}-${targetId}`}
      >
        <Radio className="w-4 h-4" />
        <span>Request</span>
      </Button>
      
      <RequestModal 
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        success={success}
        formData={formData}
        setFormData={setFormData}
        targetType={targetType}
        targetName={targetName}
        creatorName={creatorName}
        TypeIcon={TypeIcon}
      />
    </>
  );
}

// Request Modal Component
function RequestModal({ 
  isOpen, 
  onClose, 
  onSubmit,
  submitting,
  success,
  formData,
  setFormData,
  targetType,
  targetName,
  creatorName,
  TypeIcon
}) {
  if (!isOpen) return null;

  const getTypeLabel = () => {
    const labels = {
      song: 'Song',
      track: 'Song',
      video: 'Video',
      creator: 'Creator',
      artist: 'Artist',
      podcast: 'Podcast',
      podcaster: 'Podcaster',
      stream: 'Stream',
      streamer: 'Streamer',
      service: 'Service',
      studio: 'Studio',
      producer: 'Producer',
      videographer: 'Videographer',
      graphic_designer: 'Graphic Designer'
    };
    return labels[targetType?.toLowerCase()] || 'Content';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="request-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <TypeIcon className="w-5 h-5 text-primary" />
            </div>
            Request {getTypeLabel()}
          </DialogTitle>
          <DialogDescription>
            Send a request to {creatorName || 'the creator'}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center animate-in zoom-in-50">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-green-500">Request Sent!</p>
            <p className="text-sm text-muted-foreground mt-2">
              {creatorName || 'The creator'} will be notified
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Target Info */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <TypeIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Requesting:</span>
                <span className="font-medium truncate">{targetName || 'Unknown'}</span>
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message (optional)</Label>
              <Textarea
                id="message"
                placeholder="Add a message to your request..."
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                maxLength={300}
                rows={3}
                data-testid="request-message-input"
              />
              <p className="text-xs text-muted-foreground text-right">
                {formData.message.length}/300
              </p>
            </div>

            {/* Dedication */}
            <div className="space-y-2">
              <Label htmlFor="dedication">Dedicate to someone (optional)</Label>
              <Input
                id="dedication"
                placeholder="e.g., For my friend's birthday"
                value={formData.dedication}
                onChange={(e) => setFormData(prev => ({ ...prev, dedication: e.target.value }))}
                maxLength={100}
                data-testid="request-dedication-input"
              />
            </div>

            {/* Format Preference */}
            <div className="space-y-2">
              <Label>Format preference (optional)</Label>
              <Select
                value={formData.format_preference}
                onValueChange={(value) => setFormData(prev => ({ ...prev, format_preference: value }))}
              >
                <SelectTrigger data-testid="request-format-select">
                  <SelectValue placeholder="Select format..." />
                </SelectTrigger>
                <SelectContent>
                  {FORMAT_OPTIONS.map(option => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={submitting}
                data-testid="request-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Radio className="w-4 h-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RequestButton;
